import { useMemo } from 'react';

import { type InternalRefetchQueriesInclude } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, LinearProgress, useTheme } from '@mui/material';
import { Trash2Icon } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Offers_Status_Enum } from '@graphql/__generated__/graphql';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { OfferForm } from '@modules/_shared/components/offers/OfferForm';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useMlsAccessCheck } from '@modules/mls/mlsListingContext';
import {
  DELETE_OFFER_MUTATION,
  GET_OFFER_DRAWER_QUERY,
} from '@modules/offers/offersQueries';
import { useLocale } from '@shared/hooks/locale';

export const OfferDrawer: React.FC<{
  refetchQueries?: InternalRefetchQueriesInclude;
  onCreate?: (offerId: string | undefined) => void;
  // When provided, closing is delegated to the caller instead of navigating up
  // a path segment. Used when the drawer is mounted as a search-param overlay
  // (e.g. over the Buyers tab) rather than as a nested `offers/new` route.
  onClose?: () => void;
}> = ({ refetchQueries, onCreate, onClose }) => {
  const { t } = useLocale();
  const { breakpoints } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { offerId, lotId } = useParams();
  const { me } = useAppData();
  const { isMlsMode, mlsShareId } = useMlsAccessCheck();

  const { data, error, loading } = useQuery(GET_OFFER_DRAWER_QUERY, {
    variables: { id: offerId ?? '' },
    skip: offerId == null,
  });

  const handleClose = () => {
    if (onClose != null) {
      onClose();
      return;
    }

    searchParams.delete('fromListing');
    searchParams.delete('buyerId');

    navigate({
      pathname: '../',
      search: searchParams.toString(),
    });
  };

  const [deleteOffer] = useMutation(DELETE_OFFER_MUTATION);

  const canUpdateOffer =
    me?.is_admin === true || me?.id === data?.offers_by_pk?.created_by;

  const title = useMemo(() => {
    if (offerId == null) {
      return t('Create offer');
    }

    return canUpdateOffer ? t('Edit offer') : t('View offer');
  }, [offerId, canUpdateOffer, t]);

  const navigateToTransaction = (listingId: string, fromOfferId: string) => {
    const newSearchParams =
      lotId != null ? searchParams : new URLSearchParams();

    newSearchParams.delete('create_offer');
    newSearchParams.delete('buyerId');
    newSearchParams.set('selected-listing', listingId);
    newSearchParams.set('from-offer', fromOfferId);

    const basePath =
      isMlsMode && lotId != null ? `/mls/${lotId}` : `/listings/${listingId}`;

    navigate({
      pathname: `${basePath}/transaction/new/listing`,
      search: newSearchParams.toString(),
    });
  };

  const drawerActions =
    offerId != null
      ? [
          {
            label: t('Delete offer'),
            disabled: !canUpdateOffer,
            onClick: () => {
              if (
                window.confirm(t('Are you sure you want to delete this offer?'))
              ) {
                // Delete team and remove from cache
                deleteOffer({
                  variables: {
                    id: offerId,
                  },
                  update: cache => {
                    cache.evict({
                      id: `offers:${offerId}`,
                    });
                  },
                }).then(() => {
                  // Close drawer
                  handleClose();
                });
              }
            },
            icon: <Trash2Icon size={20} />,
          },
        ]
      : undefined;

  return (
    <Drawer
      onClose={handleClose}
      title={title}
      actions={drawerActions}
      maxWidth={breakpoints.values.md}
    >
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      )}
      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 2000,
          }}
        />
      )}
      {data?.offers_by_pk != null && (
        <OfferForm
          offer={data.offers_by_pk}
          readonly={!canUpdateOffer}
          preventListingChange={lotId != null}
          onCancel={isFormDirty => {
            if (!isFormDirty) {
              handleClose();
            }
          }}
          onSubmit={updateData => {
            if (
              data.offers_by_pk?.status !== updateData?.status &&
              updateData?.status === Offers_Status_Enum.Accepted
            ) {
              navigateToTransaction(updateData.lot.id, updateData.id);
            }
          }}
        />
      )}
      {offerId == null && (
        <OfferForm
          offer={null}
          prefillListing={lotId ?? searchParams.get('fromListing') ?? undefined}
          initialBuyerId={searchParams.get('buyerId') ?? undefined}
          preventListingChange={lotId != null}
          refetchQueries={refetchQueries}
          mlsShareId={mlsShareId}
          onCancel={isFormDirty => {
            if (!isFormDirty) {
              handleClose();
            }
          }}
          onSubmit={createData => {
            if (createData?.status === Offers_Status_Enum.Accepted) {
              navigateToTransaction(createData.lot.id, createData.id);
            } else {
              if (onCreate != null) {
                onCreate(createData?.id);
              } else {
                handleClose();
              }
            }
          }}
        />
      )}
    </Drawer>
  );
};
